import { useCallback, useEffect, useId, useRef, useState } from "react"
import { toast } from "sonner"
import { Checkbox } from "~/components/ui/checkbox"
import { Button } from "~/components/ui/button"
import { Textarea } from "~/components/ui/textarea"
import { fileToBase64 } from "~/lib/file-upload"
import { Input, formInputSurface } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import type { FormFieldType as FormFieldTypeValue } from "~/lib/enums"

export type FormUploadFn = (input: {
	name: string
	type: string
	data: string
}) => Promise<{ url: string }>

export type QuestionInputProps = {
	qtype: FormFieldTypeValue
	options: {
		choices?: string[]
		allowOther?: boolean
		includesTime?: boolean
		includesDate?: boolean
		min?: number
		max?: number
	} | null
	value: unknown
	placeholder?: string
	label?: string
	invalid?: boolean
	editing?: boolean
	onChoicesChange?: (choices: string[]) => void
	onChange: (value: unknown) => void
	disabled?: boolean
	upload: FormUploadFn
}

/** The per-type answer control for a form question. */
export const QuestionInput = ({
	qtype,
	options,
	value,
	onChange,
	disabled,
	upload,
	placeholder = "",
	label,
	invalid,
	editing = false,
	onChoicesChange
}: QuestionInputProps) => {
	const id = useId()
	const [focused, setFocused] = useState(false)
	const [otherText, setOtherText] = useState("")
	useEffect(() => {
		if (editing || value === null || value === undefined || value === "")
			setOtherText("")
		else if (
			typeof value === "object" &&
			"choice" in value &&
			value.choice === "Other" &&
			"text" in value &&
			typeof value.text === "string"
		)
			setOtherText(value.text)
	}, [editing, value])
	const setText = (next: string) => onChange(next)
	const choices =
		(qtype === "dropdown" || qtype === "radioGroup") &&
		options?.allowOther &&
		!(options.choices ?? []).includes("Other")
			? [...(options.choices ?? []), "Other"]
			: (options?.choices ?? [])
	if (editing) {
		if (
			qtype === "radioGroup" ||
			qtype === "dropdown" ||
			qtype === "checkboxGroup"
		)
			return (
				<div className="flex flex-col items-start gap-2">
					{choices.map((choice, index) => (
						<Input
							key={index}
							variant="form"
							aria-label={`Option ${index + 1}`}
							value={choice}
							disabled={disabled || (options?.allowOther && choice === "Other")}
							onChange={(event) =>
								onChoicesChange?.(
									(options?.choices ?? []).map((item, i) =>
										i === index ? event.target.value : item
									)
								)
							}
						/>
					))}
				</div>
			)
		if (qtype === "checkbox")
			return (
				<div className="flex items-center gap-2" aria-label="Checkbox preview">
					<span className="size-4 rounded border border-stroke" />
					Yes
				</div>
			)
		if (qtype === "signature")
			return (
				<div
					className={`${formInputSurface} flex min-h-28 items-center justify-center text-text-sub`}
				>
					Sign here
				</div>
			)
		if (qtype === "fileUpload")
			return (
				<div
					className={`${formInputSurface} flex min-h-24 items-center justify-center text-text-sub`}
				>
					Choose a file
				</div>
			)
		if (qtype === "date")
			return (
				<Input
					variant="form"
					aria-label={label || "Date preview"}
					type={
						options?.includesDate === false
							? "time"
							: options?.includesTime
								? "datetime-local"
								: "date"
					}
					value=""
					readOnly
					tabIndex={-1}
					disabled={disabled}
				/>
			)
		return (
			<div className={qtype === "longText" ? "w-full" : "w-full max-w-80"}>
				{qtype === "longText" ? (
					<Textarea
						variant="form"
						aria-label="Answer placeholder"
						value={typeof value === "string" ? value : ""}
						placeholder={focused ? "Type placeholder text" : ""}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						onChange={(event) => setText(event.target.value)}
						disabled={disabled}
					/>
				) : (
					<Input
						variant="form"
						type="text"
						aria-label="Answer placeholder"
						value={typeof value === "string" ? value : ""}
						placeholder={focused ? "Type placeholder text" : ""}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						onChange={(event) => setText(event.target.value)}
						disabled={disabled}
					/>
				)}
			</div>
		)
	}
	if (qtype === "longText")
		return (
			<Textarea
				variant="form"
				placeholder={placeholder}
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				value={typeof value === "string" ? value : ""}
				onChange={(event) => setText(event.target.value)}
				disabled={disabled}
			/>
		)
	if (qtype === "number")
		return (
			<Input
				variant="form"
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				type="number"
				placeholder={placeholder}
				min={options?.min}
				max={options?.max}
				value={
					value === null || value === undefined || value === ""
						? ""
						: String(value)
				}
				onChange={(event) =>
					onChange(
						event.target.value === "" ? null : Number(event.target.value)
					)
				}
				disabled={disabled}
			/>
		)
	if (qtype === "email")
		return (
			<Input
				variant="form"
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				type="email"
				placeholder={placeholder}
				value={typeof value === "string" ? value : ""}
				onChange={(event) => setText(event.target.value)}
				disabled={disabled}
			/>
		)
	if (qtype === "phone")
		return (
			<Input
				variant="form"
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				type="tel"
				placeholder={placeholder}
				value={typeof value === "string" ? value : ""}
				onChange={(event) => setText(event.target.value)}
				disabled={disabled}
			/>
		)
	if (qtype === "url")
		return (
			<Input
				variant="form"
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				type="url"
				placeholder={placeholder}
				value={typeof value === "string" ? value : ""}
				onChange={(event) => setText(event.target.value)}
				disabled={disabled}
			/>
		)
	if (qtype === "signature")
		return (
			<SignaturePad
				value={typeof value === "string" ? value : null}
				onChange={onChange}
				disabled={disabled}
				upload={upload}
			/>
		)
	if (qtype === "dropdown" || qtype === "radioGroup") {
		const other = Boolean(
			options?.allowOther &&
			(value === "Other" ||
				(value &&
					typeof value === "object" &&
					"choice" in value &&
					value.choice === "Other"))
		)
		const selected = other ? "Other" : typeof value === "string" ? value : ""
		return (
			<div className="flex flex-col gap-2">
				{qtype === "dropdown" ? (
					<Select
						value={selected}
						onValueChange={(next) =>
							onChange(
								options?.allowOther && next === "Other"
									? { choice: "Other", text: otherText }
									: next
							)
						}
						disabled={disabled}
					>
						<SelectTrigger
							aria-label={label || "Choose an option"}
							aria-invalid={invalid || undefined}
						>
							<SelectValue placeholder={placeholder || "Choose an option"} />
						</SelectTrigger>
						<SelectContent>
							{choices.map((choice) => (
								<SelectItem key={choice} value={choice}>
									{choice}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					<RadioGroup
						aria-label={label}
						aria-invalid={invalid || undefined}
						value={selected}
						onValueChange={(next) =>
							onChange(
								options?.allowOther && next === "Other"
									? { choice: "Other", text: otherText }
									: next
							)
						}
						disabled={disabled}
						className="gap-2"
					>
						{choices.map((choice) => (
							<Label
								key={choice}
								className="flex items-center gap-2 font-normal"
							>
								<RadioGroupItem value={choice} />
								{choice}
							</Label>
						))}
					</RadioGroup>
				)}
				{other && (
					<Input
						variant="form"
						aria-label={`Other answer for ${label || "this question"}`}
						aria-invalid={invalid || undefined}
						placeholder="Type your answer"
						value={
							value &&
							typeof value === "object" &&
							"text" in value &&
							typeof value.text === "string"
								? value.text
								: ""
						}
						onChange={(event) =>
							onChange({ choice: "Other", text: event.target.value })
						}
						disabled={disabled}
					/>
				)}
			</div>
		)
	}
	if (qtype === "checkboxGroup") {
		const selected = Array.isArray(value)
			? value.filter((choice): choice is string => typeof choice === "string")
			: []
		return (
			<div
				role="group"
				aria-label={label}
				aria-invalid={invalid || undefined}
				className="flex flex-col gap-2"
			>
				{choices.map((choice, index) => (
					<Label
						key={choice}
						htmlFor={`${id}-${index}`}
						className="flex items-start gap-2 font-normal"
					>
						<Checkbox
							id={`${id}-${index}`}
							aria-invalid={invalid || undefined}
							checked={selected.includes(choice)}
							disabled={disabled}
							onCheckedChange={(checked) =>
								onChange(
									checked === true
										? [...selected.filter((item) => item !== choice), choice]
										: selected.filter((item) => item !== choice)
								)
							}
						/>
						<span className="min-w-0 break-words">{choice}</span>
					</Label>
				))}
			</div>
		)
	}
	if (qtype === "checkbox")
		return (
			<Label className="flex items-center gap-2 font-normal">
				<Checkbox
					id={id}
					aria-label={label}
					aria-invalid={invalid || undefined}
					checked={value === true}
					onCheckedChange={(next) => onChange(next === true)}
					disabled={disabled}
				/>
				Yes
			</Label>
		)
	if (qtype === "date")
		return (
			<Input
				variant="form"
				id={id}
				aria-label={label}
				aria-invalid={invalid || undefined}
				type={
					options?.includesDate === false
						? "time"
						: options?.includesTime
							? "datetime-local"
							: "date"
				}
				value={typeof value === "string" ? value : ""}
				disabled={disabled}
				onChange={(event) => onChange(event.target.value || null)}
			/>
		)
	if (qtype === "fileUpload")
		return (
			<FormFileUpload
				label={label}
				invalid={invalid}
				value={value as { name: string; url: string } | null}
				onChange={(next) => onChange(next)}
				disabled={disabled}
				upload={upload}
			/>
		)
	return (
		<Input
			variant="form"
			id={id}
			aria-label={label}
			aria-invalid={invalid || undefined}
			type="text"
			placeholder={placeholder}
			value={typeof value === "string" ? value : ""}
			onChange={(event) => setText(event.target.value)}
			disabled={disabled}
		/>
	)
}

const SignaturePad = ({
	value,
	onChange,
	disabled,
	upload
}: {
	value: string | null
	onChange: (value: unknown) => void
	disabled?: boolean
	upload: FormUploadFn
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const drawing = useRef(false)
	const [dirty, setDirty] = useState(false)
	const [started, setStarted] = useState(false)
	const dirtyRef = useRef(false)
	const [saving, setSaving] = useState(false)

	const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current
		if (!canvas) return { x: 0, y: 0 }
		const rect = canvas.getBoundingClientRect()
		return {
			x: ((event.clientX - rect.left) / rect.width) * canvas.width,
			y: ((event.clientY - rect.top) / rect.height) * canvas.height
		}
	}

	const stroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
		if (!drawing.current || !canvasRef.current) return
		const context = canvasRef.current.getContext("2d")
		if (!context) return
		const point = pointFrom(event)
		context.strokeStyle = "#000000"
		context.lineWidth = 2.5
		context.lineCap = "round"
		context.lineTo(point.x, point.y)
		context.stroke()
	}

	const save = useCallback(async () => {
		const canvas = canvasRef.current
		if (!canvas || !dirtyRef.current || saving) return
		setSaving(true)
		try {
			const base64 = canvas.toDataURL("image/png").split(",")[1] ?? ""
			const result = await upload({
				name: `signature-${Date.now()}.png`,
				type: "image/png",
				data: base64
			})
			onChange(result.url)
			dirtyRef.current = false
			setDirty(false)
		} catch {
			toast.error("Could not save the signature. Try again.")
		} finally {
			setSaving(false)
		}
	}, [onChange, saving, upload])

	if (!started && value) {
		return (
			<div className="flex flex-col gap-1">
				<img
					src={value}
					alt="Saved signature"
					className="max-h-24 rounded-md border border-stroke bg-background p-1"
				/>
				<Button
					type="button"
					variant="link"
					size="sm"
					className="self-start"
					disabled={disabled}
					onClick={() => onChange(null)}
				>
					Redo signature
				</Button>
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-1">
			<canvas
				ref={canvasRef}
				width={420}
				height={120}
				aria-label="Signature pad"
				className="w-full max-w-105 touch-none rounded-md border border-stroke bg-background"
				onPointerDown={(event) => {
					if (disabled || saving) return
					setStarted(true)
					drawing.current = true
					const context = canvasRef.current?.getContext("2d")
					const point = pointFrom(event)
					context?.beginPath()
					context?.moveTo(point.x, point.y)
					event.currentTarget.setPointerCapture(event.pointerId)
				}}
				onPointerMove={stroke}
				onPointerCancel={() => {
					drawing.current = false
				}}
				onPointerUp={() => {
					if (!drawing.current) return
					drawing.current = false
					dirtyRef.current = true
					setDirty(true)
					void save()
				}}
			/>
			{started && (
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={disabled || saving}
						onClick={() => {
							const canvas = canvasRef.current
							if (canvas)
								canvas
									.getContext("2d")
									?.clearRect(0, 0, canvas.width, canvas.height)
							dirtyRef.current = false
							setDirty(false)
							onChange(null)
						}}
					>
						Clear
					</Button>
					{saving ? (
						<span className="text-xs text-text-sub">Saving…</span>
					) : (
						dirty && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={disabled}
								onClick={() => void save()}
							>
								Retry saving signature
							</Button>
						)
					)}
				</div>
			)}
		</div>
	)
}

const FormFileUpload = ({
	label,
	invalid,
	value,
	onChange,
	disabled,
	upload
}: {
	label?: string
	invalid?: boolean
	value: { name: string; url: string } | null
	onChange: (value: unknown) => void
	disabled?: boolean
	upload: FormUploadFn
}) => (
	<div className="flex flex-col gap-1">
		<Input
			variant="file"
			type="file"
			aria-label={label || "Choose a file"}
			aria-invalid={invalid || undefined}
			disabled={disabled}
			onChange={async (event) => {
				const file = event.target.files?.[0]
				if (!file) return
				try {
					if (file.size > 5 * 1024 * 1024) throw new Error("File exceeds 5 MB")
					const data = await fileToBase64(file)
					const result = await upload({
						name: file.name.slice(0, 255),
						type: file.type || "application/octet-stream",
						data
					})
					onChange({ name: file.name, url: result.url })
				} catch {
					toast.error("Upload failed. Keep the file under 5 MB and try again.")
				}
			}}
		/>
		{value && <p className="text-xs text-text-sub">{value.name}</p>}
	</div>
)
